import { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";

import { faBuilding, faInbox, faListCheck, faUsers } from "@fortawesome/pro-regular-svg-icons";

import { Icon } from "@/components/Icon/Icon";

import { TrackerEntityKind } from "@/enum/tracker.enum";

import {
	useDeleteTrackerFileMutation,
	useUpdateTrackerFileMutation,
} from "@/pages/Private/redux/profileTracker/profileTracker.api";

import {
	useDeleteTrackerListImportMutation,
	useLazyDownloadSkippedTrackerRowsQuery,
	useUpdateTrackerListImportMutation,
} from "@/pages/Private/redux/profileTracker/trackerSources.api";

import { TrackerListImport } from "../../../ProfileTracker/schema/profileTracker";
import { TrackerFileInfo } from "../../schema/list-tracker.schema";

import { SourceMetric, TrackerSourceCard } from "./TrackerSourceCard";

interface TrackerAddedSourcesProps {
	files: TrackerFileInfo[];
	listImports: TrackerListImport[];
}

const toCount = (value?: number) => Number(value ?? 0) || 0;

/**
 * Everything currently feeding the tracker — uploaded files and imported lists side by side
 * (PREC-3417), each reporting what it actually produced.
 */
export const TrackerAddedSources: FunctionComponent<TrackerAddedSourcesProps> = ({
	files,
	listImports,
}) => {
	const { t } = useTranslation();

	const [updateTrackerFile] = useUpdateTrackerFileMutation();
	const [deleteTrackerFile] = useDeleteTrackerFileMutation();
	const [updateListImport] = useUpdateTrackerListImportMutation();
	const [deleteListImport] = useDeleteTrackerListImportMutation();
	const [downloadSkipped] = useLazyDownloadSkippedTrackerRowsQuery();

	const total = files.length + listImports.length;

	const handleDownloadSkipped = async (trackerFile: TrackerFileInfo) => {
		const blob = await downloadSkipped(trackerFile.id).unwrap();
		const url = URL.createObjectURL(blob);
		const link = document.createElement("a");

		link.href = url;
		link.download = `skipped-rows-${trackerFile.id}.xlsx`;
		link.click();
		URL.revokeObjectURL(url);
	};

	if (total === 0) {
		return (
			<div className="flex flex-col items-center justify-center px-5 py-12 text-center">
				<div className="mb-3 flex h-11 w-11 items-center justify-center rounded-[11px] bg-brand-white text-text-meta">
					<Icon className="h-4 w-4" icon={faInbox} />
				</div>
				<div className="text-sm font-semibold text-text-meta">
					{t("profileTracker.sources.added.emptyTitle")}
				</div>
				<div className="mt-1 max-w-[280px] text-ssm text-text-meta">
					{t("profileTracker.sources.added.emptyDescription")}
				</div>
			</div>
		);
	}

	return (
		<div>
			{files.map((file) => {
				const isCompanies = file.entityKind === TrackerEntityKind.ORGANIZATION;
				const skipped = toCount(file.skippedCount);
				const isActive = file.isActive !== false;

				const metrics: SourceMetric[] = [
					{ label: t("profileTracker.sources.added.rows"), value: toCount(file.rowCount) },
					{
						label: t("profileTracker.sources.added.ready"),
						value: toCount(file.readyCount),
						tone: "ready",
					},
					{
						label: t("profileTracker.sources.added.skipped"),
						value: skipped,
						tone: skipped > 0 ? "skipped" : "neutral",
					},
				];

				return (
					<TrackerSourceCard
						key={`file-${file.id}`}
						icon={<Icon className="h-4 w-4" icon={isCompanies ? faBuilding : faUsers} />}
						iconClassName={
							isCompanies
								? "bg-status-success-light text-status-success"
								: "bg-blue-action-200 text-brand-light"
						}
						footerText={file.name}
						meta={
							isCompanies
								? t("profileTracker.sources.added.companies")
								: t("profileTracker.sources.added.people")
						}
						metrics={metrics}
						name={file.name}
						removeLabel={t("profileTracker.sources.added.remove")}
						skippedNotice={
							skipped > 0
								? {
										text: t(
											`profileTracker.sources.added.${
												isCompanies ? "skippedCompanies" : "skippedPeople"
											}`,
											{ count: skipped }
										),
										onDownload: () => handleDownloadSkipped(file),
									}
								: undefined
						}
						statusLabel={
							isActive
								? t("profileTracker.sources.added.tracking")
								: t("profileTracker.sources.added.paused")
						}
						statusTone={isActive ? "tracking" : "paused"}
						toggle={{
							label: t("profileTracker.sources.added.trackingActive"),
							isChecked: isActive,
							onChange: () => updateTrackerFile({ id: file.id, isActive: !isActive }),
						}}
						onRemove={() => deleteTrackerFile(file.id)}
					/>
				);
			})}

			{listImports.map((listImport) => {
				const isCompanies = listImport.entityKind === TrackerEntityKind.ORGANIZATION;

				const metrics: SourceMetric[] = [
					{
						label: t("profileTracker.sources.added.matched"),
						value: toCount(listImport.matchedCount),
					},
					{
						label: t("profileTracker.sources.added.imported"),
						value: toCount(listImport.importedCount),
						tone: "ready",
					},
				];

				return (
					<TrackerSourceCard
						key={`import-${listImport.id}`}
						icon={<Icon className="h-4 w-4" icon={faListCheck} />}
						iconClassName="bg-tag-light text-brand-light"
						footerText={[
							t("profileTracker.sources.added.importedList"),
							isCompanies
								? t("profileTracker.sources.added.companies")
								: t("profileTracker.sources.added.people"),
							t(`profileTracker.sources.import.presets.${listImport.priorityPreset}`),
						].join(" · ")}
						meta={listImport.listName ?? ""}
						metrics={metrics}
						name={listImport.listName ?? ""}
						removeLabel={t("profileTracker.sources.added.remove")}
						statusLabel={
							listImport.isLive
								? t("profileTracker.sources.added.liveImport")
								: t("profileTracker.sources.added.tracking")
						}
						statusTone={listImport.isLive ? "live" : "tracking"}
						toggle={{
							label: t("profileTracker.sources.added.liveSyncOn"),
							isChecked: listImport.isLive,
							onChange: () => updateListImport({ id: listImport.id, isLive: !listImport.isLive }),
						}}
						onRemove={() => deleteListImport(listImport.id)}
					/>
				);
			})}
		</div>
	);
};
